import { action } from 'satcheljs';
import {
    type OWAConnectedAccount,
    type OWAConnectedAccountState,
    type AccountProviderType,
} from './store/schema/OWAConnectedAccount';
import type { AccountsStoreState } from './store/schema/AccountsStoreState';

/**
 * Adds an account to the store.
 * The store responds by loading the user configuration of this account.
 */
export interface AddAccountActionMessage {
    userIdentity: string;
    account: OWAConnectedAccount;
}
export const addOWAConnectedAccount: (account: OWAConnectedAccount) => AddAccountActionMessage =
    action(
        'addAccount',
        (account: OWAConnectedAccount): AddAccountActionMessage => ({
            userIdentity: account.userIdentity,
            account,
        })
    );

/**
 * Removes an account to the store.
 */
export interface RemoveAccountActionMessage {
    userIdentity: string;
}
export const removeOWAConnectedAccount: (userIdentity: string) => RemoveAccountActionMessage =
    action(
        'removeAccount',
        (userIdentity: string): RemoveAccountActionMessage => ({
            userIdentity,
        })
    );

/**
 * Updates the OWA Connected account status
 */
export interface UpdateOWAConnectedAccountStatusActionMessage {
    accountKeyOrUserIdentity: string;
    accountState: OWAConnectedAccountState;
}
export const updateOWAConnectedAccountStatus: (
    accountKeyOrUserIdentity: string,
    accountState: OWAConnectedAccountState
) => UpdateOWAConnectedAccountStatusActionMessage = action(
    'updateOWAConnectedAccountStatus',
    (accountKeyOrUserIdentity: string, accountState: OWAConnectedAccountState) => ({
        accountKeyOrUserIdentity,
        accountState,
    })
);

/**
 * Triggered whenever connected account is determined to be in error state
 */
export interface ConnectedAccountInErrorStateActionMessage {
    accountState: OWAConnectedAccountState;
    accountProvider: AccountProviderType;
}
export const connectedAccountInErrorState: (
    accountState: OWAConnectedAccountState,
    accountProvider: AccountProviderType
) => ConnectedAccountInErrorStateActionMessage = action(
    'connectedAccountInErrorState',
    (accountState: OWAConnectedAccountState, accountProvider: AccountProviderType) => ({
        accountState,
        accountProvider,
    })
);

/**
 * Announces that the getConnectedAccounts API has returned with a response
 * @param isSuccess to be true if all of the connected accounts returned from the server
 */
export interface ConnectedAccountsListRequestedActionMessage {
    isSuccess: boolean;
}
export const connectedAccountsListRequested: (
    isSuccess: boolean
) => ConnectedAccountsListRequestedActionMessage = action(
    'connectedAccountsListRequested',
    (isSuccess: boolean): ConnectedAccountsListRequestedActionMessage => ({ isSuccess })
);

export interface SetAccountsStoreStateActionMessage {
    state: AccountsStoreState;
}
export const setAccountsStoreState: (
    state: AccountsStoreState
) => SetAccountsStoreStateActionMessage = action(
    'setAccountsStoreState',
    (state: AccountsStoreState): SetAccountsStoreStateActionMessage => ({ state })
);
